export interface TranscriptSegment {
  id: string
  timeSec: number | null
  label: string
  speakerNumber: number
  name: string | null
  text: string
}

// Speaker marker in link form `[Name](spk_0):` or raw `spk_0:` (optional `\[00:14\]`
// timestamp prefix). Identity is the `spk_N` target, not the display name.
// 1=min 2=sec 3=name(link) 4=spk(link) 5=spk(raw)
const SEGMENT_RE = /(?:\\?\[(\d{1,3}):(\d{2})\\?\]\s*)?(?:\[([^\]]*)\]\((spk_\d+)\)|(spk_\d+))\s*:\s*/g

// Matches `spk_0:` and the applied `[Name](spk_0):` link form. True for
// AWS-Transcribe output (uploaded video/audio), not plain YouTube captions.
const SPEAKER_LABEL_RE = /(?:spk_\d+|\[[^\]]*\]\(spk_\d+\))\s*:/m

export function transcriptHasSpeakerLabels(content: string | undefined | null): boolean {
  return !!content && SPEAKER_LABEL_RE.test(content)
}

export function speakerNumberFromLabel(label: string): number {
  const parsed = parseInt(label.replace('spk_', ''), 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

function cleanSegmentText(raw: string): string {
  return raw
    .replace(/\\(?=\s|$)/g, '') // drop line-continuation backslashes
    .replace(/\s+/g, ' ')
    .trim()
}

function toSeconds(min?: string, sec?: string): number | null {
  if (min == null || sec == null) return null
  return parseInt(min, 10) * 60 + parseInt(sec, 10)
}

/** Split a speaker-labeled transcript into ordered segments keyed by `spk_N`. */
export function parseTranscriptSegments(raw: string): TranscriptSegment[] {
  const markers: {
    index: number
    end: number
    label: string
    name: string | null
    timeSec: number | null
  }[] = []
  SEGMENT_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = SEGMENT_RE.exec(raw)) !== null) {
    const label = m[4] ?? m[5]
    if (!label) continue
    const name = m[3]?.trim() || null
    markers.push({ index: m.index, end: SEGMENT_RE.lastIndex, label, name, timeSec: toSeconds(m[1], m[2]) })
  }
  return markers.map((mk, i) => {
    const textEnd = i + 1 < markers.length ? markers[i + 1].index : raw.length
    return {
      id: `seg-${i}`,
      label: mk.label,
      speakerNumber: speakerNumberFromLabel(mk.label),
      name: mk.name,
      timeSec: mk.timeSec,
      text: cleanSegmentText(raw.slice(mk.end, textEnd)),
    }
  })
}

export function formatTime(totalSec: number | null): string {
  if (totalSec == null) return '--:--'
  const minutes = Math.floor(totalSec / 60)
  const seconds = Math.floor(totalSec % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}
